import { useContext, useMemo } from 'react';
import { UXPContext } from '@core/context/Context';
import { useAddOns } from '@helper/AddOn/useAddOns';
import { hasValue, textSearch, isUserAllowed } from '@utils';
import { getWidgetStatus, filterByStatus } from '@core/dashboard/widget-drawer/widgetDrawerUtils';
import { WidgetDrawerType, WidgetDrawerStatus } from '@core/dashboard/widget-drawer/WidgetDrawer';
import useWidgetCache from './useWidgetCache';
const PAGE_SIZE = 50;
/**
 * @export
 * Widget drawer specific hook for filtering, pagination, and search
 * Uses useWidgetCache internally for data management
 */
function useDrawerItems(options) {
    const context = useContext(UXPContext);
    const addOns = useAddOns();
    // Use cache hook for data
    const { allWidgets: cachedWidgets, allUIs: cachedUIs, allCategories, isLoading, reload } = useWidgetCache({
        enableUIs: options === null || options === void 0 ? void 0 : options.enableUIs
    });
    /**
     * Filter out items the current user has no permission for (appRoles/userGroups).
     * Array.isArray guards against loosely-typed API payloads (see tags-as-string precedent).
     */
    const allWidgets = useMemo(() => cachedWidgets.filter(w => isUserAllowed(context, Array.isArray(w.userGroups) ? w.userGroups : [], Array.isArray(w.appRoles) ? w.appRoles : [])), [cachedWidgets, context]);
    const allUIs = useMemo(() => cachedUIs.filter(u => isUserAllowed(context, Array.isArray(u.userGroups) ? u.userGroups : [], Array.isArray(u.appRoles) ? u.appRoles : [])), [cachedUIs, context]);
    /**
     * Filter and paginate items based on current options
     */
    const filteredItems = useMemo(() => {
        let items = [];
        // Select widgets or UIs
        if (options.type === WidgetDrawerType.Widgets) {
            items = allWidgets;
        }
        else {
            items = allUIs;
        }
        // Apply search
        if (hasValue(options.searchQuery)) {
            items = items.filter(item => {
                const name = item.name || item.label;
                return textSearch(name, options.searchQuery) ||
                    textSearch(item.localizedName, options.searchQuery) ||
                    textSearch(item.description, options.searchQuery) ||
                    textSearch(item.localizedDescription, options.searchQuery) ||
                    textSearch(item.id, options.searchQuery);
            });
        }
        // Apply status filter (widgets only)
        if (options.type === WidgetDrawerType.Widgets && options.status !== WidgetDrawerStatus.All) {
            items = filterByStatus(items, options.status, options.existingWidgets, addOns);
        }
        // Apply category filter (widgets only)
        if (options.type === WidgetDrawerType.Widgets && options.category !== '__all__') {
            const cat = allCategories.find(c => c.id === options.category);
            if (cat) {
                items = items.filter(w => { var _a; return (_a = cat.subCategories) === null || _a === void 0 ? void 0 : _a.includes(w.category); });
            }
        }
        // Paginate widgets (UIs show all)
        if (options.type === WidgetDrawerType.Widgets) {
            const endIndex = (options.page + 1) * PAGE_SIZE;
            return items.slice(0, endIndex);
        }
        return items;
    }, [allWidgets, allUIs, allCategories, options, addOns]);
    /**
     * Calculate total count before pagination
     */
    const totalCountBeforePagination = useMemo(() => {
        let items = [];
        // Select widgets or UIs
        if (options.type === WidgetDrawerType.Widgets) {
            items = allWidgets;
        }
        else {
            items = allUIs;
        }
        // Apply search
        if (hasValue(options.searchQuery)) {
            items = items.filter(item => {
                const name = item.name || item.label;
                return textSearch(name, options.searchQuery) ||
                    textSearch(item.localizedName, options.searchQuery) ||
                    textSearch(item.description, options.searchQuery) ||
                    textSearch(item.localizedDescription, options.searchQuery) ||
                    textSearch(item.id, options.searchQuery);
            });
        }
        // Apply status filter (widgets only)
        if (options.type === WidgetDrawerType.Widgets && options.status !== WidgetDrawerStatus.All) {
            items = filterByStatus(items, options.status, options.existingWidgets, addOns);
        }
        // Apply category filter (widgets only)
        if (options.type === WidgetDrawerType.Widgets && options.category !== '__all__') {
            const cat = allCategories.find(c => c.id === options.category);
            if (cat) {
                items = items.filter(w => { var _a; return (_a = cat.subCategories) === null || _a === void 0 ? void 0 : _a.includes(w.category); });
            }
        }
        return items.length;
    }, [allWidgets, allUIs, allCategories, options, addOns]);
    /**
     * Update category counts based on current filters
     */
    const categoriesWithCounts = useMemo(() => {
        // Filter by search query
        let filtered = allWidgets;
        if (hasValue(options.searchQuery)) {
            filtered = allWidgets.filter((w) => textSearch(w.name, options.searchQuery) ||
                textSearch(w.localizedName, options.searchQuery) ||
                textSearch(w.description, options.searchQuery) ||
                textSearch(w.localizedDescription, options.searchQuery) ||
                textSearch(w.id, options.searchQuery));
        }
        // Apply status filter for counts
        if (options.status !== WidgetDrawerStatus.All) {
            filtered = filterByStatus(filtered, options.status, options.existingWidgets, addOns);
        }
        // Update category counts
        return allCategories.map((c) => (Object.assign(Object.assign({}, c), { count: c.id === '__all__'
                ? filtered.length
                : filtered.filter((w) => { var _a; return (_a = c.subCategories) === null || _a === void 0 ? void 0 : _a.includes(w.category); }).length })));
    }, [allWidgets, allCategories, options.searchQuery, options.status, options.existingWidgets, addOns]);
    /**
     * Calculate counts for all filter options
     */
    const counts = useMemo(() => {
        return {
            widgets: allWidgets.length,
            uis: allUIs.length,
            statuses: {
                all: allWidgets.length,
                installed: allWidgets.filter(w => getWidgetStatus(w, options.existingWidgets, addOns) === 'installed').length,
                added: allWidgets.filter(w => getWidgetStatus(w, options.existingWidgets, addOns) === 'added').length,
                notInstalled: allWidgets.filter(w => getWidgetStatus(w, options.existingWidgets, addOns) === 'not-installed').length,
            }
        };
    }, [allWidgets, allUIs, options.existingWidgets, addOns]);
    /**
     * Check if there are more items to load (for infinite scroll)
     */
    const hasMore = useMemo(() => {
        if (options.type === WidgetDrawerType.UIs)
            return false; // UIs show all at once
        return totalCountBeforePagination > filteredItems.length;
    }, [options.type, totalCountBeforePagination, filteredItems.length]);
    return {
        items: filteredItems,
        totalCount: options.type === WidgetDrawerType.Widgets ? allWidgets.length : allUIs.length,
        filteredCount: totalCountBeforePagination,
        isLoading,
        hasMore,
        categories: categoriesWithCounts,
        reload,
        counts
    };
}
export default useDrawerItems;
